import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { CheckIcon, SlidersHorizontalIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

type FilterProps = {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	children: React.ReactNode;
};

const Filter = ({ open, onOpenChange, children }: FilterProps) => {
	return (
		<Popover open={open} onOpenChange={onOpenChange}>
			{children}
		</Popover>
	);
};

type FilterTriggerProps = {
	name: string;
	selectedValues: Set<string>;
	options: Array<{ value: string; label: string }>;
	icon?: React.ReactNode;
};

const FilterTrigger = ({
	selectedValues,
	options,
	name,
	icon = <SlidersHorizontalIcon />,
}: FilterTriggerProps) => {
	const { t } = useTranslation();
	return (
		<PopoverTrigger asChild>
			<Button variant="outline">
				{icon}
				{name}
				{selectedValues.size === 1 && (
					<>
						<Separator orientation="vertical" className="mx-2 h-4" />
						{options
							.filter((option) => selectedValues.has(option.value))
							.map((option) => (
								<Badge
									variant="secondary"
									key={option.value}
									className="rounded-sm px-1 font-normal"
								>
									{option.label}
								</Badge>
							))}
					</>
				)}
				{selectedValues.size > 1 && (
					<>
						<Separator orientation="vertical" className="mx-2 h-4" />
						<Badge variant="secondary" className="rounded-sm px-1 font-normal">
							{selectedValues.size} {t("admin.filter.selected")}
						</Badge>
					</>
				)}
			</Button>
		</PopoverTrigger>
	);
};

type FilterContentProps = {
	children: React.ReactNode;
	className?: string;
	selectedSize: number;
	clearSelected: () => void;
};

const FilterContent = ({
	children,
	className,
	selectedSize,
	clearSelected,
}: FilterContentProps) => {
	const { t } = useTranslation();
	return (
		<PopoverContent className={cn("p-0 w-fit", className)}>
			<Command className="w-fit">
				<CommandInput
					className="border-none outline-none focus-visible:ring-0"
					placeholder={t("admin.filter.searchPlaceholder")}
				/>
				<CommandList>
					<CommandEmpty>{t("admin.filter.noResults")}</CommandEmpty>
					<CommandGroup>{children}</CommandGroup>
					{selectedSize > 0 && (
						<>
							<CommandSeparator />
							<CommandGroup>
								<CommandItem
									onSelect={clearSelected}
									className="justify-center text-center"
								>
									{t("admin.filter.clearFilters")}
								</CommandItem>
							</CommandGroup>
						</>
					)}
				</CommandList>
			</Command>
		</PopoverContent>
	);
};

type FilterItemProps = {
	option: { value: string; label: string };
	isSelected: boolean;
	toggleValue: (value: string) => void;
};

const FilterItem = ({ option, isSelected, toggleValue }: FilterItemProps) => (
	<CommandItem key={option.value} onSelect={() => toggleValue(option.value)}>
		<div
			className={cn(
				"mr-2 flex h-4 w-4 items-center justify-center dark:border-gray-400 rounded-sm border border-gray-600",
				isSelected
					? "bg-gray-900 dark:bg-gray-100 text-primary-foreground"
					: "opacity-50 [&_svg]:invisible"
			)}
		>
			<CheckIcon className="h-4 w-4" />
		</div>
		<span>{option.label}</span>
	</CommandItem>
);

export { Filter, FilterContent, FilterItem, FilterTrigger };
